/**
 * extension.js — the portal's client + React hook for the Keep Me Out browser extension.
 *
 * Implements the `window.postMessage` handshake defined in the extension's
 * `KeepMeOut-BrowserTools/docs/adr/0005-portal-detection-and-daily-sync.md` (and verified by that
 * repo's Playwright smoke test). This lets keepmeout.app / portal.keepmeout.app answer the owner's
 * requirement: "when the portal opens, detect whether the extension is installed and pull its data."
 *
 * This is NOT egress — it stays entirely within the user's browser (this tab ↔ the extension's content
 * script), so it works today with no backend and returns only category-level counts (never raw hosts).
 *
 * This module is the single source of truth for extension detection: BOTH the marketing `/extension`
 * page (via `src/components/ExtensionStatus`) and the portal dashboard (via
 * `src/app/components/BrowserExtension`) import `useExtension` from here, so the handshake logic lives
 * in exactly one place.
 */
import { useEffect, useState } from 'react';

export const KMO_SOURCE_EXT = 'kmo-ext';
export const KMO_SOURCE_PORTAL = 'kmo-portal';
export const KMO_V = 1;

const post = (msg) => {
  try {
    window.postMessage({ source: KMO_SOURCE_PORTAL, v: KMO_V, ...msg }, window.location.origin);
  } catch (e) {
    /* postMessage can throw in exotic sandboxes — treat as "no extension" */
  }
};

/**
 * Watch for the extension's presence announcement. Calls `onPresent({browser, version, capabilities})`
 * when the extension replies. Pings a few times (the content script may load before or after us).
 * Returns an unsubscribe function.
 */
export function watchExtension(onPresent) {
  const handler = (e) => {
    if (e.source !== window) return;
    const d = e.data;
    if (!d || d.source !== KMO_SOURCE_EXT || d.type !== 'KMO_EXT_PRESENT') return;
    onPresent({ browser: d.browser, version: d.version, capabilities: d.capabilities || [] });
  };
  window.addEventListener('message', handler);
  post({ type: 'KMO_PING' });
  const timers = [setTimeout(() => post({ type: 'KMO_PING' }), 300), setTimeout(() => post({ type: 'KMO_PING' }), 1200)];
  return () => {
    window.removeEventListener('message', handler);
    timers.forEach(clearTimeout);
  };
}

/**
 * Ask the extension for TODAY's local data. Resolves with `{ today, usage }` (or null on timeout):
 *  - `today`  — category-level counts `{ attempts:{adult,custom}, blocked, resisted, bypassAttempts,
 *               tamperEvents }` (never raw hosts).
 *  - `usage`  — the day's usage rollup `{ totalActiveSec, topSites:[{host,sec}], pairingCode, … }`
 *               (per-site active time; the extension only fills it once the user has paired). May be
 *               null on older extensions that predate the usage rollup.
 * We return BOTH (the earlier client dropped `usage` on the floor) so the portal can surface time-on-site
 * as well as attempt counts.
 */
export function requestLocalPull(timeoutMs = 2500) {
  return new Promise((resolve) => {
    const handler = (e) => {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || d.source !== KMO_SOURCE_EXT || d.type !== 'KMO_LOCAL_PULL_RESULT') return;
      cleanup();
      resolve({ today: d.today || null, usage: d.usage || null });
    };
    const to = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const cleanup = () => {
      window.removeEventListener('message', handler);
      clearTimeout(to);
    };
    window.addEventListener('message', handler);
    post({ type: 'KMO_LOCAL_PULL', scope: 'today' });
  });
}

/**
 * React hook: run the presence handshake on mount and, once the extension answers, pull today's data.
 * Returns `{ present, info, today, usage }` where `info = { browser, version, capabilities }`.
 *
 * Presence has no negative signal — an absent extension simply never replies — so `present` stays false
 * until an announcement arrives. Callers that want to show an "install" prompt should settle on their own
 * timer (see BrowserExtension's `settled`), since "not yet" and "not installed" look identical here.
 */
export function useExtension() {
  const [info, setInfo] = useState(null);
  const [today, setToday] = useState(null);
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    const unsub = watchExtension(async (i) => {
      setInfo(i);
      const pull = await requestLocalPull();
      if (pull) {
        if (pull.today) setToday(pull.today);
        if (pull.usage) setUsage(pull.usage);
      }
    });
    return unsub;
  }, []);
  return { present: !!info, info, today, usage };
}

/**
 * Hand a link ticket (ADR-0049) straight to the co-located extension. A browser has no camera, so
 * instead of a QR we push the ticket over this same origin-scoped bridge and the extension redeems it
 * itself — one click, nothing to type. Resolves `{ ok, reason, userId }` on success, `{ ok:false, error }`
 * on failure, or null on timeout. Safe because only the AUTHENTICATED portal can mint a ticket, and the
 * bridge only exists on keepmeout.app origins.
 *
 * `reason` matters as much as `ok` (ADR-0051 R26). The extension answers `already-linked` — with `ok:true`
 * — when it is already a device of some account. If that account is not OURS, nothing was linked and the
 * user has two independent ecosystems; reporting it as a plain success is how the fork stayed invisible.
 * See `isForkedLinkResult` below.
 */
export function sendLinkTicket(ticket, timeoutMs = 15000) {
  return new Promise((resolve) => {
    const handler = (e) => {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || d.source !== KMO_SOURCE_EXT || d.type !== 'KMO_LINK_RESULT') return;
      cleanup();
      resolve({ ok: !!d.ok, reason: d.reason, userId: d.userId, error: d.error });
    };
    const to = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const cleanup = () => {
      window.removeEventListener('message', handler);
      clearTimeout(to);
    };
    window.addEventListener('message', handler);
    post({ type: 'KMO_LINK', ticket });
  });
}

/**
 * Did that link attempt just reveal an ACCIDENTAL SECOND ACCOUNT? (ADR-0051 R26)
 *
 * This is the whole detection story, and it is deliberately this small. The SERVER can never answer
 * "are these two `user_id`s the same human" — there is no email, phone number or ad-id to correlate on,
 * by design (ADR-0035). But a browser CAN, locally, because it can see both ids at once: the account the
 * portal is signed into, and the account the extension sitting in the same browser enrolled itself under.
 * Two different ids here means one person is running two ecosystems, and the extension refused the link
 * (`already-linked`) rather than silently re-homing itself.
 *
 * That refusal is correct — re-homing would orphan everything already queued under the old id — but the
 * portal used to render it as "this browser is now linked to your account", which was simply untrue. This
 * function is what turns that moment into an honest question instead.
 *
 * @param {{ok:boolean, reason?:string, userId?:string}|null} res the `sendLinkTicket` result
 * @param {string|null|undefined} myUserId the user_id THIS portal session is signed in as
 */
export function isForkedLinkResult(res, myUserId) {
  return !!(res && res.reason === 'already-linked' && res.userId && myUserId && res.userId !== myUserId);
}

/**
 * Ask the extension to mint an ACCOUNT-MERGE correlator as its own account (ADR-0051, side X).
 *
 * The ceremony is two-sided because the server refuses to guess: the human proves both accounts are theirs
 * by holding a live credential on each inside a 5-minute window. So the extension authenticates itself for
 * `start`, and this portal authenticates itself for `preview` and `confirm`. Neither side ever sees the
 * other's token — all that crosses this bridge is the ticket.
 *
 * Resolves `{ ok, reason, ticket?, userId? }`, or null on timeout. `reason: 'merge-disabled'` is the
 * EXPECTED answer today: the extension's own switch is off, so it refuses before touching the network.
 *
 * @param {string|null} portalUserId our own user_id, so the extension can refuse a self-merge on-device
 */
export function requestMergeStart(portalUserId, timeoutMs = 15000) {
  return new Promise((resolve) => {
    const handler = (e) => {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || d.source !== KMO_SOURCE_EXT || d.type !== 'KMO_MERGE_START_RESULT') return;
      cleanup();
      resolve({ ok: !!d.ok, reason: d.reason, ticket: d.ticket, userId: d.userId });
    };
    const to = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const cleanup = () => {
      window.removeEventListener('message', handler);
      clearTimeout(to);
    };
    window.addEventListener('message', handler);
    post({ type: 'KMO_MERGE_START', portalUserId: portalUserId || null });
  });
}

/** Best-effort current-browser detection for the download page (order matters: derivatives first). */
export function detectBrowser() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (typeof navigator !== 'undefined' && navigator.brave) return 'brave';
  if (/Edg\//.test(ua)) return 'edge';
  if (/OPR\/|Opera/.test(ua)) return 'opera';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'chrome';
}

/**
 * Per-browser install targets. URLs are empty until the store listings exist (Phase 5) — the UI shows
 * "coming soon" for empty ones. Brave/Opera install the Chrome build.
 */
export const STORE_LINKS = {
  chrome: { label: 'Chrome', icon: '🌐', store: 'Chrome Web Store', url: '' },
  edge: { label: 'Edge', icon: '🌊', store: 'Edge Add-ons', url: '' },
  firefox: { label: 'Firefox', icon: '🦊', store: 'Firefox Add-ons', url: '' },
  safari: { label: 'Safari', icon: '🧭', store: 'Mac App Store', url: '' },
  brave: { label: 'Brave', icon: '🦁', store: 'Chrome Web Store', url: '' },
  opera: { label: 'Opera', icon: '🎭', store: 'Chrome Web Store', url: '' },
};
